import { AppStateService } from './_core/app-state.service';
import { SocketService } from './_core/services/socket.service';
import { Component, inject } from '@angular/core';
import { NavController, Platform } from '@ionic/angular';

import { Capacitor } from '@capacitor/core';
// import {
//     ActionPerformed,
//     PushNotificationSchema,
//     PushNotifications,
//     Token
// } from '@capacitor/push-notifications';
import { App } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { Browser } from '@capacitor/browser';
import { Device } from '@capacitor/device';

import { TranslateService } from '@ngx-translate/core';
import { AuthService } from './auth/auth.service';
import { FirebaseAnalytics } from '@capacitor-community/firebase-analytics';
import { environment, FIREBASE_ANALYTICS } from '../environments/environment';
import { User } from './_shared/models/user.model';
import { ThemeService } from './_core/services/theme.service';
import { AppService } from './app.service';
import { LanguageService } from './_core/services/language.service';
import { InAppReview } from '@capacitor-community/in-app-review';
import { Preferences } from '@capacitor/preferences';
import { v4 as uuidv4 } from 'uuid';
import { Router } from '@angular/router';
import { Network } from '@capacitor/network';
import {
  FirebaseMessaging,
  PermissionStatus,
} from '@capacitor-firebase/messaging';
// import { AdMob } from '@capacitor-community/admob';
import { IS_NEW_UI_FLOW } from '../environments/environment';
import { HelperService } from './_core/services/helper.service';
import { ToastService } from './_core/services/toast.service';
import { EdgeToEdge } from '@capawesome/capacitor-android-edge-to-edge-support';

@Component({
  standalone: false,
  selector: 'app-root',
  templateUrl: 'app.component.html',
  providers: [AppStateService],
})
export class AppComponent {
  info: any;
  authUser: User | undefined;
  isDarkMode: boolean;
  version: string;
  isOnline = true;

  private onlineHandler = () => {
    this.isOnline = true;
    const currentUrl = this.router.url;
    const isOnNoInternetPage = currentUrl.includes('/no-internet');
    if (isOnNoInternetPage) {
      this.router.navigate(['/']);
    }
  };

  constructor(
    private platform: Platform,
    private navCtrl: NavController,
    private toastCtrl: ToastService,
    private translateService: TranslateService,
    public auth: AuthService,
    public themeService: ThemeService,
    private appService: AppService,
    private socketService: SocketService,
    private router: Router,
    private languageService: LanguageService,
    private helperService: HelperService
  ) {
    this.initializeApp();
    this.appService.checkForUpdates();
    window.addEventListener('online', this.onlineHandler);
    window.addEventListener('offline', () => {
      this.isOnline = false;
    });
  }

  async initializeApp() {
    // Ensure language is properly initialized
    this.languageService.ensureLanguageInitialized();

    // Store Current app version
    localStorage.setItem('appVersion', await this.getAppVersion());

    // Clear invalid tokens on app start
    await this.clearInvalidTokens();

    // Get current dark mode
    this.isDarkMode = await this.themeService.getIsDarkTheme();
    let deviceId = (await Preferences.get({ key: 'deviceId' }))?.value;

    this.auth.authUser$.subscribe((user) => {
      this.authUser = user;
    });

    const status = await Network.getStatus();

    if (!status.connected && Capacitor.isNativePlatform()) {
      this.router.navigate(['/no-internet']);
    }

    if (!deviceId) {
      deviceId = uuidv4();
      await Preferences.set({ key: 'deviceId', value: deviceId || '' });
    }

    // if (this.platform.is('android') || this.platform.is('ios')) {
    //   await this.initializeAdMob();
    // }

    if (Capacitor.isNativePlatform()) {
      const info = await Device.getInfo();
      if (Capacitor.getPlatform() === 'android' ) {
        if (Number(info.osVersion) >= 12) {
          await EdgeToEdge.enable(); // Enable only on Android 12+
        } else {
          await EdgeToEdge.disable(); // Prevents layout issues on older Android versions
        }
      }

      await StatusBar.setStyle({
        style: this.isDarkMode ? Style.Dark : Style.Light,
      });
      await StatusBar.show();

      this.deeplinkInit();

      // Listen for app state changes to handle SSO callback
      App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          const currentRoute = this.router.url;
          // Only check authentication if we're on the SSO callback page
          if (currentRoute.includes('/sso-callback')) {
            this.checkAuthenticationAfterSSO();
          } else {
          }
        } else {
        }
      });

      // Test URL scheme handling
      // console.log('Testing URL scheme handling...');
      // setTimeout(() => {
      //   console.log('URL scheme test: ioncourt://sso-callback?code=test123');
      //   // You can test the URL scheme by manually typing this in Safari:
      //   // ioncourt://sso-callback?code=test123
      // }, 2000);
    }

    if (await this.auth.isAuthenticated()) {
      this.authUser = await this.auth.getAuthUser();
      if (Capacitor.isNativePlatform()) {
        await this.initPushNotifications();
        // Ask users to rate if they have been using app for at least 30 days
        if (this.authUser?.createdAt) {
          const date = new Date(this.authUser.createdAt);
          date.setDate(date.getDate() + 30);
          if (date < new Date() && environment.production) {
            await InAppReview.requestReview();
          }
        }
      }
    }

    if (environment.production) {
      await FirebaseAnalytics.initializeFirebase(FIREBASE_ANALYTICS);
    }

    this.auth.subscribeLoggedIn().subscribe((isLoggedIn) => {
      if (isLoggedIn) {
        if (Capacitor.isNativePlatform()) {
          this.initPushNotifications();
        }
      }
    });
    this.socketService.connectionStatus();
    await SplashScreen.hide();
  }

   // private async initializeAdMob(): Promise<void> {
   //  try {
   //    await AdMob.initialize();
   //    const trackingInfo = await AdMob.trackingAuthorizationStatus();
   //    if (trackingInfo.status === 'notDetermined') {
   //      await AdMob.requestTrackingAuthorization();
   //    }
   //  } catch (error) {
   //    console.error('AdMob initialization failed:', error);
   //  }
   // }

  async deeplinkInit() {
    await App.addListener('appUrlOpen', (data: any) => {
      // Handle SSO callback
      if (data.url.includes('sso-callback')) {
        try {
          const url = new URL(data.url);
          const code = url.searchParams.get('code');
          const error = url.searchParams.get('error');
          const errorDescription = url.searchParams.get('error_description');

          // Navigate to SSO callback component with query parameters
          const queryParams = new URLSearchParams();
          if (code) queryParams.set('code', code);
          if (error) queryParams.set('error', error);
          if (errorDescription) queryParams.set('error_description', errorDescription);

          const queryString = queryParams.toString();
          const route = `/${IS_NEW_UI_FLOW ? 'auth' : 'login'}/sso-callback${queryString ? '?' + queryString : ''}`;
          this.navCtrl.navigateRoot(route);
        } catch (error) {
          // Fallback: navigate to SSO callback without parameters
          this.navCtrl.navigateRoot(`/${IS_NEW_UI_FLOW ? 'auth' : 'login'}/sso-callback`);
        }
      } else {
        // Handle other deep links
        try {
          // Parse URL to extract the path
          this.handleScorekeeperAssignment(data.url);
          const url = new URL(data.url);
          const route = url.pathname;
          // Store returnTo URL for deep links (like /join/{team})
          if (route && route !== '/' && route.includes('/join')) {
            localStorage.setItem('returnTo', route);
          }
          this.navCtrl.navigateRoot(route);
        } catch (error) {
          this.toastCtrl.presentDangerToast(error ?? 'Something went wrong!')
        }
      }
    });

    /*
    // Handle app launch with URL (when returning from external browser)
    var urlOpen = await App.getLaunchUrl();
    if (urlOpen && urlOpen.url) {
      // Handle SSO callback on app launch
      if (urlOpen.url.includes('sso-callback')) {
        const url = new URL(urlOpen.url);
        const code = url.searchParams.get('code');
        const error = url.searchParams.get('error');
        const errorDescription = url.searchParams.get('error_description');

        const queryParams = new URLSearchParams();
        if (code) queryParams.set('code', code);
        if (error) queryParams.set('error', error);
        if (errorDescription) queryParams.set('error_description', errorDescription);

        const queryString = queryParams.toString();
        const route = `/${IS_NEW_UI_FLOW ? 'auth' : 'login'}/sso-callback${queryString ? '?' + queryString : ''}`;
        this.navCtrl.navigateRoot(route);
      } else {
        // Parse URL to extract the path
        const url = new URL(urlOpen.url);
        const deepLinkRoute = url.pathname;

        // Store returnTo URL for deep links (like /join/{team})
        if (deepLinkRoute && deepLinkRoute !== '/' && deepLinkRoute.includes('/join')) {
          localStorage.setItem('returnTo', deepLinkRoute);
        }

        this.navCtrl.navigateForward(deepLinkRoute);
      }

      // clear launch URL after the page is called;
      // Note: urlOpen.url is read-only, so we can't clear it
    } else {
    }
    */
  }

  async handleScorekeeperAssignment(openUrl: string) {
    if (openUrl) {
      const url = new URL(openUrl);
      const [ct, cid, tmId, ms] = ['ct', 'cid', 'tmId', 'ms'].map(k => url.searchParams.get(k));
      if (ct && cid) {
        this.helperService.setScorekeeperContext(cid, ct, tmId, ms);
      }
    }
  }

  async getAppVersion() {
    if (Capacitor.isNativePlatform()) {
      const appInfo = await App.getInfo();
      this.version = appInfo.version;
      return appInfo.version;
    } else {
      return 'browser';
    }
  }

  private async initPushNotifications() {
    const pushPermissionsStatus = (await this.checkPermissions()).receive;

    if (pushPermissionsStatus === 'prompt') {
      if ((await this.requestPermissions())?.receive === 'granted') {
        setTimeout(async () => {
          await this.getPushToken();
        }, 3000);
      }
    } else if (pushPermissionsStatus === 'granted') {
      await this.getPushToken();
    }

    FirebaseMessaging.addListener('notificationActionPerformed', (event: any) => {
      const data: any = event.notification?.data;
      if (data) {
        if (data.route) {
          this.navCtrl.navigateForward(data.route);
        } else {
          if (data.matchId && data.type === 'live-scoring') {
            this.navCtrl.navigateForward('live-scoring/' + data.matchId);
          } else if (data.tieId && data.type === 'team-match') {
            this.navCtrl.navigateForward('ties/' + data.tieId);
          } else if (data.tournamentId && data.type === 'tournament') {
            this.navCtrl.navigateForward('tournaments/' + data.tournamentId + '/live-score');
          }
        }
      }
    });

    FirebaseMessaging.addListener('tokenReceived', async (event) => {
      await this.getPushToken(event.token);
    });

    // FirebaseMessaging.addListener('notificationReceived', (event) => {
    //   const data: any = event.notification.data;
    //   if (data.route) {
    //     this.navCtrl.navigateForward(data.route);
    //   }
    // });

    // PushNotifications.requestPermissions().then(async result => {
    //     if (result.receive === 'granted') {
    //         await PushNotifications.register();
    //     }
    // });

    // PushNotifications.addListener('registration',
    //     async (token: Token) => {
    //         localStorage.setItem('device-token', token.value);

    //         const deviceInfo = await Device.getInfo();
    //         const deviceId = await Device.getId();

    //         const pushObj = {
    //             token: token.value,
    //             fingerprint: deviceId.identifier,
    //             os: deviceInfo.platform,
    //             osVersion: deviceInfo.osVersion,
    //             device: deviceInfo.model,
    //             deviceType: this.platform.is('mobile') ? 'mobile' : 'tablet',
    //             deviceVendor: deviceInfo.manufacturer,
    //         };

    //         this.auth.registerDevice(pushObj).subscribe();
    //     }
    // );

    // PushNotifications.addListener('registrationError',
    //     (error: any) => {
    //         // alert('Error on registration: ' + JSON.stringify(error));
    //     }
    // );

    // PushNotifications.addListener('pushNotificationReceived',
    //     async (push: PushNotificationSchema) => {
    //         const toast = await this.toastCtrl.create({
    //             header: push.title,
    //             message: push.body,
    //             duration: 2000,
    //             position: 'top'
    //         });
    //         await toast.present();
    //     }
    // );

    // PushNotifications.addListener('pushNotificationActionPerformed',
    //     (push: ActionPerformed) => {
    //         if (push.notification.data.matchId && push.notification.data.type === 'live-scoring') {
    //             this.navCtrl.navigateForward('live-scoring/' + push.notification.data.matchId);
    //         } else if (push.notification.data.tieId && push.notification.data.type === 'team-match') {
    //             this.navCtrl.navigateForward('ties/' + push.notification.data.tieId);
    //         }
    //     }
    // );
  }

  async getPushToken(fcmToken: string | null = null) {
    let token: string | undefined;
    if (fcmToken === null) {
      const result = await FirebaseMessaging.getToken();
      token = result?.token;
    } else {
      token = fcmToken;
    }
    if (!token) {
      //console.warn('No FCM token retrieved');
      return;
    }
    localStorage.setItem('device-token', token);

    const deviceInfo = await Device.getInfo();
    const deviceId = await Device.getId();

    const pushObj = {
      token: token,
      fingerprint: deviceId.identifier,
      os: deviceInfo.platform,
      osVersion: deviceInfo.osVersion,
      device: deviceInfo.model,
      deviceType: this.platform.is('mobile') ? 'mobile' : 'tablet',
      deviceVendor: deviceInfo.manufacturer,
    };

    this.auth.registerDevice(pushObj).subscribe();
  }

  async checkPermissions(): Promise<PermissionStatus> {
    return await FirebaseMessaging.checkPermissions();
  }

  async requestPermissions(): Promise<PermissionStatus> {
    return await FirebaseMessaging.requestPermissions();
  }

  async openSocial(social) {
    let link = '';
    if (social === 'facebook') link = 'https://www.facebook.com/ioncourtapp';
    else if (social === 'instagram') link = 'https://www.instagram.com/ioncourtapp';
    else if (social === 'twitter') link = 'https://www.twitter.com/ioncourtapp';

    if (link) {
      await Browser.open({
        url: link,
      });
    }
  }

  dismissOffline() {
    this.isOnline = true;
  }

  private async checkAuthenticationAfterSSO() {
    // Check if user is authenticated after returning from SSO
    if (await this.auth.isAuthenticated()) {
      const authUser = await this.auth.getAuthUser();
      this.authUser = authUser;
      // Only navigate to main menu if we're still on the SSO callback page
      const currentRoute = this.router.url;
      if (currentRoute.includes('/sso-callback')) {
        // Check for returnTo URL first (for /join/{team} redirects)
        const returnTo = localStorage.getItem('returnTo');
        if (returnTo) {
          localStorage.removeItem('returnTo');
          this.navCtrl.navigateRoot(returnTo);
        } else {
          this.navCtrl.navigateRoot('/main-menu');
        }
      } else {
      }
    } else {
    }
  }

  private async clearInvalidTokens() {
    try {
      const token = localStorage.getItem('token');
      const accessToken = localStorage.getItem('accessToken');
      const cognitoUsername = localStorage.getItem('cognitoUsername');      

      // Only clear JWT tokens if they're clearly invalid
      if (token && !accessToken) {
        try {
          const decodedToken = this.auth.jwtHelper.decodeToken(token);
          if (!decodedToken) {
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
          } else if (this.auth.jwtHelper.isTokenExpired(token)) {
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
          } else {
            //console.log('Valid JWT token found');
          }
        } catch (error) {
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
        }
      }

      // Don't clear Cognito tokens on app start, let the auth service handle them
      if (accessToken && cognitoUsername) {
        //console.log('Cognito tokens found, will validate during authentication check');
      }

    } catch (error) {
      //console.log('Error checking tokens on app start:', error);
    }
  }
}